import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { Getter } from 'vuex-class';

import TkForm from '@/../bower_components/teknisavuecomponents/components/TkForm.vue';
import TkDate from '@/../bower_components/teknisavuecomponents/components/fields/TkDate.vue';
import Field from '@/../bower_components/teknisavuecomponents/assets/interfaces/Field';
import HttpRequest from '@/../bower_components/teknisavuecomponents/assets/utils/HttpRequest';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import User from '@/../bower_components/teknisavuecomponents/assets/interfaces/User';
import { MedicoController } from '@/assets/ts/controller/MedicoController';
import DocumentoUtil from '@/assets/ts/utils/DocumentoUtil';

@Component({
  name: 'NovoMedicoPopup',
  components: {
    TkForm,
    TkDate,
  },
})
class NovoMedicoPopup extends Vue {
  // #region [ PROPS ]
  @Prop({ default: false })
  private readonly value: boolean;
  // #endregion

  // #region [ STORE ]
  @Getter('user')
  private user: User;
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-save')
  private emitAfterSave(medico: any) {
    return medico;
  }

  @Emit('input')
  private emitInput(value: boolean): boolean {
    return value;
  }
  // #endregion

  // #region [ DATA ]
  public internalValue = false;
  public loading = false;
  public conselhosRegionais: any[] = [];
  public estados: any[] = [];

  public row: any = {
    NMPESSOA: '',
    NRCPFPESSOA: '',
    NRCONSELHOREG: '',
    NRINSCRCONSREG: '',
    SGUFCONSREG: '',
    DTEXCONSREG: '',
    DTINIVIGENCIA: '01/01/2000',
    DTFIMVIGENCIA: '',
    __is_new: true,
};

  public fields: Field[] = [
    {

        name: 'NMPESSOA',
        label:'Nome',
        type: 'text',
        size: 8,
        required: true,
    },
    {
        name:'NRCPFPESSOA',
        label:'CPF',
        type: 'text',
        size: 4,
        required: true,
        mask: '###.###.###-##',
    },
    {
        name:'NRCONSELHOREG',
        label:'Conselho Regional',
        type:'select',
        size: 6,
        valueField: 'NRCONSELHOREG',
        descriptionField: 'NMCONSELHOREG',
        route: 'getConselhoRegional',
        reloadOnCreation: false,
        filters: {
          NRORG: '',
          NRORG_PADRAO: 0,
        },
        required: true,
    },
    {
        name:'NRINSCRCONSREG',
        label:'Inscrição no Conselho',
        type: 'text',
        size: 6,
        required: true,
    },
    {
        name:'DTEXCONSREG',
        label:'Dt. Ex. Conselho',
        type: 'date',
        size: 6,
        required: false,
    },
    {
        name:'SGUFCONSREG',
        label:'UF Conselho',
        type:'select',
        size: 6,
        valueField: 'SGESTADO',
        descriptionField: 'NMESTADO',
        route: 'getEstado',
        reloadOnCreation: false,
        filters: {
        NRORG: '',
        CDPAIS: '0055',
        },
        required: true,
    },
    {
        name:'DTINIVIGENCIA',
        label:'Início Vigência',
        type: 'date',
        size: 6,
        required: true,
    },
    {
        name:'DTFIMVIGENCIA',
        label:'Fim Vigência',
        type: 'date',
        size: 6,
        required: false,
    },
];
  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private valueOnChange() {
    this.internalValue = this.value;
  }

  @Watch('internalValue')
  private async onInternalValueChange(newValue: boolean) {
    if (newValue) {
      try {
        await this.carregarConselhosRegionais();
        await this.carregarEstados();
        // Forçar a atualização da interface após carregar os dados
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    }
  }
  // #endregion

  // #region [ METHODS ]
  public closePopup() {
    this.internalValue = false;
    this.emitInput(this.internalValue);
    this.resetForm();
  }

  private resetForm() {
    this.row = {
        NMPESSOA: '',
        NRCPFPESSOA: '',
        NRCONSELHOREG: '',
        NRINSCRCONSREG: '',
        SGUFCONSREG: '',
        DTEXCONSREG: '',
        DTINIVIGENCIA: '01/01/2000',
        DTFIMVIGENCIA: '',
        __is_new: true,
    };
}

  // Método para carregar os dados de Conselho Regional
  private async carregarConselhosRegionais() {
    try {
      const route = 'getConselhoRegional';
      const params = {
        requestType: 'FilterData',
        NRORG: this.user && this.user.nrorg ? this.user.nrorg : '',
        NRORG_PADRAO: 0,
      };

      this.loading = true;

      const response = await HttpRequest.get(route, params, false);

      if (response.data && response.data.dataset) {
        const rows = response.data.dataset.data || response.data.dataset[route] || [];

        if (rows.length === 0) {
          SnackbarUtils.showMessage('Não foram encontrados dados de Conselho Regional', 'warning');
        } else {
          // Armazenar os dados no array
          this.conselhosRegionais = rows;

          // Atualizar o campo no formulário diretamente
          const conselhoIndex = this.fields.findIndex(f => f.name === 'NRCONSELHOREG');
          if (conselhoIndex >= 0) {
            // Criar uma cópia do campo atual
            const updatedField = { ...this.fields[conselhoIndex] };

            // Atualizar as propriedades
            updatedField.items = rows;

            // Substituir o campo no array
            this.$set(this.fields, conselhoIndex, updatedField);

            // Forçar a atualização da interface
            this.$forceUpdate();
          }
        }
      } else {
        console.error('Formato de resposta inválido para getConselhoRegional:', response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de Conselho Regional:', error);
    } finally {
      this.loading = false;
    }
  }

  // Método para carregar os dados de Estado
  private async carregarEstados() {
    try {
      const route = 'getEstado';
      const params = {
        requestType: 'FilterData',
        NRORG: this.user && this.user.nrorg ? this.user.nrorg : '',
        CDPAIS: '0055',
      };

      this.loading = true;
      const response = await HttpRequest.get(route, params, false);

      if (response.data && response.data.dataset) {
        const rows = response.data.dataset.data || response.data.dataset[route] || [];

        if (rows.length === 0) {
          SnackbarUtils.showMessage('Não foram encontrados dados de Estado', 'warning');
        } else {
          // Armazenar os dados no array
          this.estados = rows;

          // Atualizar o campo no formulário diretamente
          const estadoIndex = this.fields.findIndex(f => f.name === 'SGUFCONSREG');
          if (estadoIndex >= 0) {
            // Criar uma cópia do campo atual
            const updatedField = { ...this.fields[estadoIndex] };

            // Atualizar as propriedades
            updatedField.items = rows;

            // Substituir o campo no array
            this.$set(this.fields, estadoIndex, updatedField);

            // Forçar a atualização da interface
            this.$forceUpdate();
          }
        }
      } else {
        console.error('Formato de resposta inválido para getEstado:', response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de Estado:', error);
    } finally {
      this.loading = false;
    }
  }

  public async save() {
    if (!(this.row as any).validate()) {
      SnackbarUtils.showMessage('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    // Validar CPF
    if (!DocumentoUtil.validaCPF(this.row.NRCPFPESSOA)) {
      SnackbarUtils.showMessage('CPF inválido', 'error');
      return;
    }

    this.loading = true;

    try {
      // Reynaldo Guedes - Comentado em 16/06/2025 - A requisição nem sequer retorna o campo NRCPFPESSOA
      // Verificar se já existe médico com este CPF
      // const cpfExistente = await MedicoController.verificarMedicoExistente(this.row.NRCPFPESSOA);
      // if (cpfExistente) {
      //   SnackbarUtils.showMessage('Já existe um médico cadastrado com este CPF', 'error');
      //   return;
      // }

      const response = await MedicoController.cadastrarMedico(this.row);

      if (response) {
        SnackbarUtils.showMessage('Médico cadastrado com sucesso', 'success');
        this.emitAfterSave(response);
        this.closePopup();
      }
    } catch (error) {
      // Erro já tratado no MedicoController
    } finally {
      this.loading = false;
    }
  }
  // #endregion
}

export default NovoMedicoPopup;
